import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo } from 'react';
import { generatePath, RouteE } from 'src/constants/pages.constants';
import { ShareModal } from 'src/features/common/app/modals/share-modal';
import { useCollectionById } from 'src/features/common/collection/hooks/collection.hooks';
import { useAvatarAssets } from 'src/features/common/collection/hooks/use-avatar-assets';
import { useBannerAssets } from 'src/features/common/collection/hooks/use-banner-assets';
import {
    useFetchCollectionStatistics,
    useStatistics,
} from 'src/features/common/collection/hooks/use-collection-statistics';
import { useProfile, useProfileLoaded } from 'src/features/common/profile/hooks/profile.hooks';
import { useProfileLoginUrl } from 'src/features/common/profile/hooks/use-profile-login-url';
import { useUniversePermissions } from 'src/features/common/universe/hooks/use-universe-permissions';
import { useDisplayName } from 'src/features/common/user/hooks/use-display-name.hooks';
import { useUserListEntities } from 'src/features/common/user/hooks/user-list.hooks';
import { Button } from 'src/ui/common/atoms/button';
import { EntityLayout } from 'src/ui/common/layouts/entity-layout';
import { Visible } from 'src/ui/common/layouts/visible';
import { decodeHTMLEntities } from 'src/utils/html.utils';
import { StyledAuthor, StyledCollectionHeaderWrapper, StyledCollectionStatistics } from './styles';

export const CollectionDetailsContainer: React.VFC<{ collectionId: string }> = ({ collectionId }) => {
    const profileLoaded = useProfileLoaded();
    const profile = useProfile();
    const { canCreate } = useUniversePermissions();
    const router = useRouter();
    const collection = useCollectionById(collectionId);
    const userEntities = useUserListEntities();
    const { t } = useTranslation(['common']);
    const bannerAssets = useBannerAssets({ collection: collection! });
    const previewAssets = useAvatarAssets({ collection: collection! });
    const fetchStat = useFetchCollectionStatistics();
    const statistics = useStatistics();

    const owner = collection && userEntities[collection.ownerId];
    const ownerName = useDisplayName(owner);

    const locale = useMemo(
        () => ({
            by: t('labels.by', { ns: 'common' }),
        }),
        [t]
    );

    const isOwner = useMemo(() => owner?.id === profile?.user.id, [owner?.id, profile?.user.id]);

    const ownerLink = useProfileLoginUrl(owner);

    const openShareModal = useCallback(() => {
        ShareModal.open({
            previewImg: collection?.previewAssets?.webps?.mediumUrl ?? collection?.previewAssets?.originalUrl!,
            title: collection?.name!,
            subTitle: `${locale.by} ${ownerName}`,
            url: generatePath(RouteE.COLLECTION_VIEW, { id: collectionId }),
        });
    }, [locale, ownerName, collection, collectionId]);

    const handleEditCollectionClick = useCallback(async () => {
        await router.push(generatePath(RouteE.COLLECTION_EDIT, { id: collectionId }));
    }, [collectionId, router]);

    useEffect(() => {
        fetchStat([collectionId]);
    }, [fetchStat, collectionId]);

    if (!collection) {
        return null;
    }

    return (
        <EntityLayout>
            <StyledCollectionHeaderWrapper>
                <EntityLayout.Banner
                    url={bannerAssets.src}
                    videoUrl={bannerAssets.videoSrc}
                    retinaUrl={bannerAssets.retina}
                />
                {profileLoaded && isOwner && canCreate && (
                    <Button onClick={handleEditCollectionClick} variant="white">
                        Edit collection
                    </Button>
                )}
            </StyledCollectionHeaderWrapper>
            <EntityLayout.Container>
                <EntityLayout.Avatar url={previewAssets.src} retinaUrl={previewAssets.retina} />
                <div>
                    <Visible view={['laptop', 'desktop']}>
                        <StyledCollectionStatistics data={statistics[collectionId]} />
                    </Visible>
                </div>
                <EntityLayout.Socials socialNetworks={collection.socialNetworks} openShareModal={openShareModal} />
            </EntityLayout.Container>
            <EntityLayout.Title title={decodeHTMLEntities(collection.name)} />
            {ownerName && (
                <StyledAuthor tag="div" size="xs">
                    <span>{locale.by}</span>
                    <Link href={decodeHTMLEntities(ownerLink)} prefetch={false}>
                        <a>{decodeHTMLEntities(ownerName)}</a>
                    </Link>
                </StyledAuthor>
            )}
            <EntityLayout.Description description={collection.description} />
            <Visible view="mobile">
                <StyledCollectionStatistics data={statistics[collectionId]} />
            </Visible>
        </EntityLayout>
    );
};